if (!customElements.get('wi-testimonial-stories')) {
  class WITestimonialStories extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.resizeObserver = null;
      this.triggers = [];
      this.slides = [];
      this.videos = [];
      this.measureRafId = null;

      this.maxScroll = 0;
      this.targetScroll = 0;
      this.railRafId = null;
      this.railLastTimestamp = 0;
      this.isRailAnimating = false;
      this.scrollStateTimer = null;

      this.dragPointerId = null;
      this.dragCandidate = false;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartScroll = 0;
      this.dragLastScroll = 0;
      this.dragLastTime = 0;
      this.dragVelocity = 0;
      this.dragTrigger = null;

      this.pointerPress = null;

      this.activeIndex = 0;
      this.lastFocusedElement = null;
      this.openedAt = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartedOnVideoControls = false;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.rail = this.querySelector('[data-testimonial-rail]');
      this.content = this.querySelector('[data-testimonials-content]');
      this.scrollButton = this.querySelector('[data-scroll-to-testimonials]');
      this.triggers = [...this.querySelectorAll('[data-testimonial-trigger]')];
      this.modal = this.querySelector('[data-testimonial-modal]');

      if (!this.rail || !this.modal) return;

      this.slides = [...this.modal.querySelectorAll('[data-testimonial-slide]')];
      this.slidesContainer = this.modal.querySelector('[data-modal-slides]');
      this.counter = this.modal.querySelector('[data-modal-counter]');
      this.closeButton = this.modal.querySelector('.wi-testimonial-stories__close');
      this.videos = [...this.modal.querySelectorAll('.wi-testimonial-stories__video')];

      this.wheelEnabled = this.dataset.wheelScroll === 'true';
      this.shouldAutoplayVideos = this.dataset.videoAutoplay === 'true';
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;

      this.abortController = new AbortController();

      if (this.modal.parentElement !== document.body) {
        this.modal.dataset.wiPortal = 'true';
        document.body.appendChild(this.modal);
      }

      this.bindEvents();
      this.scheduleMeasure();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.rail);
      }

      this.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.scheduleMeasure(), {
            once: true,
            signal: this.abortController.signal
          });
        }
      });
    }

    disconnectedCallback() {
      this.destroy();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => this.handleRootClick(event), {
        capture: true,
        signal
      });

      this.addEventListener('pointerdown', (event) => this.trackPointerDown(event), {
        capture: true,
        signal
      });
      this.addEventListener('pointermove', (event) => this.trackPointerMove(event), {
        capture: true,
        signal
      });
      this.addEventListener('pointerup', (event) => this.trackPointerUp(event), {
        capture: true,
        signal
      });
      this.addEventListener('pointercancel', () => {
        if (this.pointerPress?.trigger) this.suppressTriggerClick(this.pointerPress.trigger, 500);
        this.pointerPress = null;
      }, {
        capture: true,
        signal
      });

      this.rail.addEventListener('wheel', (event) => this.handleWheel(event), {
        passive: false,
        signal
      });
      this.rail.addEventListener('scroll', () => this.handleNativeScroll(), {
        passive: true,
        signal
      });
      this.rail.addEventListener('pointerdown', (event) => this.handleDragStart(event), { signal });
      this.rail.addEventListener('pointermove', (event) => this.handleDragMove(event), {
        passive: false,
        signal
      });
      this.rail.addEventListener('pointerup', (event) => this.finishDrag(event), { signal });
      this.rail.addEventListener('pointercancel', (event) => this.finishDrag(event), { signal });
      this.rail.addEventListener('lostpointercapture', (event) => this.finishDrag(event), { signal });
      this.rail.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.rail.addEventListener('keydown', (event) => this.handleRailKeydown(event), { signal });

      this.modal.addEventListener('click', (event) => this.handleModalClick(event), { signal });
      this.modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      }, { signal });
      this.modal.addEventListener('close', () => this.afterModalClose(), { signal });

      this.videos.forEach((video) => this.bindVideo(video, signal));

      this.slidesContainer?.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartedOnVideoControls = false;

        const target = event.target instanceof Element ? event.target : null;
        const touchedVideo = target?.closest('.wi-testimonial-stories__video');

        if (touchedVideo) {
          const bounds = touchedVideo.getBoundingClientRect();
          const controlsZone = Math.min(82, Math.max(54, bounds.height * 0.16));
          this.touchStartedOnVideoControls = touch.clientY >= bounds.bottom - controlsZone;
        }
      }, { passive: true, signal });

      this.slidesContainer?.addEventListener('touchend', (event) => {
        if (this.touchStartedOnVideoControls) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.renderSlide(deltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
      }, { passive: true, signal });

      document.addEventListener('keydown', (event) => this.handleDocumentKeydown(event), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAllVideos(false);
      }, { signal });

      this.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
        const trigger = this.triggers.find((item) => item.dataset.blockId === blockId);
        if (trigger) this.openModal(Number(trigger.dataset.index || 0), trigger);
      }, { signal });

      const onReducedMotionChange = (event) => {
        this.reducedMotion = event.matches;
        this.stopRailAnimation(true);
      };

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', onReducedMotionChange, { signal });
      } else {
        this.reducedMotionQuery.addListener(onReducedMotionChange);
        signal.addEventListener('abort', () => {
          this.reducedMotionQuery.removeListener(onReducedMotionChange);
        }, { once: true });
      }
    }

    scheduleMeasure() {
      if (this.measureRafId) return;

      this.measureRafId = requestAnimationFrame(() => {
        this.measureRafId = null;
        this.measure();
      });
    }

    measure() {
      if (!this.rail) return;

      this.maxScroll = Math.max(this.rail.scrollWidth - this.rail.clientWidth, 0);
      const current = this.clampScroll(this.rail.scrollLeft);

      if (!this.isRailAnimating && !this.isDragging) {
        this.targetScroll = current;
      } else {
        this.targetScroll = this.clampScroll(this.targetScroll);
      }

      if (Math.abs(this.rail.scrollLeft - current) > 0.5) {
        this.rail.scrollLeft = current;
      }
    }

    clampScroll(value) {
      return Math.min(Math.max(Number(value) || 0, 0), this.maxScroll);
    }

    normalizeWheelDelta(event) {
      const multiplier = event.deltaMode === 1
        ? 16
        : event.deltaMode === 2
          ? Math.max(this.rail.clientWidth, 1)
          : 1;

      const deltaX = event.deltaX * multiplier;
      const deltaY = event.deltaY * multiplier;

      if (event.shiftKey && Math.abs(deltaY) > Math.abs(deltaX)) return deltaY;
      return Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
    }

    handleWheel(event) {
      if (!this.wheelEnabled || event.ctrlKey || this.maxScroll <= 1) return;

      const rawDelta = this.normalizeWheelDelta(event);
      if (!Number.isFinite(rawDelta) || Math.abs(rawDelta) < 0.5) return;

      const current = this.rail.scrollLeft;
      const base = this.isRailAnimating ? this.targetScroll : current;
      const movingForward = rawDelta > 0;
      const canMove = movingForward
        ? current < this.maxScroll - 0.5 || base < this.maxScroll - 0.5
        : current > 0.5 || base > 0.5;

      if (!canMove) return;

      event.preventDefault();

      this.targetScroll = this.clampScroll(base + rawDelta * 1.08);
      this.markRailScrolling();
      this.startRailAnimation();
    }

    startRailAnimation() {
      if (this.reducedMotion) {
        this.rail.scrollLeft = this.targetScroll;
        this.isRailAnimating = false;
        this.finishRailScrollingSoon();
        return;
      }

      if (this.railRafId) return;

      this.isRailAnimating = true;
      this.railLastTimestamp = 0;
      this.railRafId = requestAnimationFrame((timestamp) => this.animateRail(timestamp));
    }

    animateRail(timestamp) {
      this.railRafId = null;

      if (!this.railLastTimestamp) this.railLastTimestamp = timestamp;
      const deltaSeconds = Math.min((timestamp - this.railLastTimestamp) / 1000, 0.05);
      this.railLastTimestamp = timestamp;

      const current = this.rail.scrollLeft;
      const difference = this.targetScroll - current;

      if (Math.abs(difference) <= 0.35) {
        this.rail.scrollLeft = this.targetScroll;
        this.isRailAnimating = false;
        this.railLastTimestamp = 0;
        this.finishRailScrollingSoon();
        return;
      }

      const easing = 1 - Math.exp(-18 * deltaSeconds);
      this.rail.scrollLeft = current + difference * easing;
      this.markRailScrolling();

      this.railRafId = requestAnimationFrame((nextTimestamp) => this.animateRail(nextTimestamp));
    }

    stopRailAnimation(syncTarget = true) {
      if (this.railRafId) {
        cancelAnimationFrame(this.railRafId);
        this.railRafId = null;
      }

      this.isRailAnimating = false;
      this.railLastTimestamp = 0;

      if (syncTarget && this.rail) {
        this.targetScroll = this.clampScroll(this.rail.scrollLeft);
      }
    }

    markRailScrolling() {
      this.rail.classList.add('is-scrolling');

      if (this.scrollStateTimer) {
        clearTimeout(this.scrollStateTimer);
        this.scrollStateTimer = null;
      }
    }

    finishRailScrollingSoon() {
      if (this.scrollStateTimer) clearTimeout(this.scrollStateTimer);

      this.scrollStateTimer = window.setTimeout(() => {
        if (!this.isDragging && !this.isRailAnimating) {
          this.rail.classList.remove('is-scrolling');
        }
        this.scrollStateTimer = null;
      }, 140);
    }

    handleNativeScroll() {
      if (!this.isRailAnimating && !this.isDragging) {
        this.targetScroll = this.clampScroll(this.rail.scrollLeft);
      }

      this.markRailScrolling();
      this.finishRailScrollingSoon();
    }

    handleDragStart(event) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.stopRailAnimation(true);

      this.dragPointerId = event.pointerId;
      this.dragCandidate = true;
      this.isDragging = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartScroll = this.rail.scrollLeft;
      this.dragLastScroll = this.rail.scrollLeft;
      this.dragLastTime = performance.now();
      this.dragVelocity = 0;
      this.dragTrigger = event.target instanceof Element
        ? event.target.closest('[data-testimonial-trigger]')
        : null;
    }

    handleDragMove(event) {
      if (!this.dragCandidate || event.pointerId !== this.dragPointerId) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      if (!this.isDragging) {
        if (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX) * 1.1) {
          this.cancelDragCandidate();
          return;
        }

        if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

        this.isDragging = true;
        this.rail.classList.add('is-dragging');
        this.markRailScrolling();

        if (this.pointerPress?.pointerId === event.pointerId) {
          this.pointerPress.moved = true;
        }

        try {
          this.rail.setPointerCapture?.(event.pointerId);
        } catch (error) {
          /* Pointer capture is an enhancement, not a requirement. */
        }
      }

      event.preventDefault();

      const nextScroll = this.clampScroll(this.dragStartScroll - deltaX);
      const now = performance.now();
      const elapsed = Math.max(now - this.dragLastTime, 1);

      this.dragVelocity = (nextScroll - this.dragLastScroll) / elapsed;
      this.dragLastScroll = nextScroll;
      this.dragLastTime = now;
      this.rail.scrollLeft = nextScroll;
      this.targetScroll = nextScroll;
    }

    finishDrag(event) {
      if (this.dragPointerId === null) return;
      if (event?.pointerId !== undefined && event.pointerId !== this.dragPointerId) return;

      const pointerId = this.dragPointerId;
      const wasDragging = this.isDragging;

      this.dragPointerId = null;
      this.dragCandidate = false;
      this.isDragging = false;
      this.rail.classList.remove('is-dragging');

      try {
        if (this.rail.hasPointerCapture?.(pointerId)) {
          this.rail.releasePointerCapture(pointerId);
        }
      } catch (error) {
        /* Safe to ignore when capture has already been released. */
      }

      if (wasDragging) {
        this.suppressTriggerClick(this.dragTrigger, 650);

        const momentumDistance = this.dragVelocity * 240;
        this.targetScroll = this.clampScroll(this.rail.scrollLeft + momentumDistance);

        if (Math.abs(this.targetScroll - this.rail.scrollLeft) > 0.5) {
          this.startRailAnimation();
        } else {
          this.finishRailScrollingSoon();
        }
      } else {
        this.targetScroll = this.clampScroll(this.rail.scrollLeft);
        this.finishRailScrollingSoon();
      }

      this.dragTrigger = null;
      this.dragVelocity = 0;
    }

    cancelDragCandidate() {
      this.dragPointerId = null;
      this.dragCandidate = false;
      this.isDragging = false;
      this.dragTrigger = null;
      this.rail.classList.remove('is-dragging');
      this.finishRailScrollingSoon();
    }

    handleRailKeydown(event) {
      const amount = Math.max(this.rail.clientWidth * 0.65, 240);
      let destination = null;

      if (event.key === 'ArrowRight') {
        destination = this.targetScroll + amount;
      } else if (event.key === 'ArrowLeft') {
        destination = this.targetScroll - amount;
      } else if (event.key === 'Home') {
        destination = 0;
      } else if (event.key === 'End') {
        destination = this.maxScroll;
      }

      if (destination === null) return;

      event.preventDefault();
      this.targetScroll = this.clampScroll(destination);
      this.markRailScrolling();
      this.startRailAnimation();
    }

    trackPointerDown(event) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const target = event.target instanceof Element ? event.target : null;
      const trigger = target?.closest('[data-testimonial-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      this.pointerPress = {
        trigger,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        moved: false
      };
    }

    trackPointerMove(event) {
      if (!this.pointerPress || this.pointerPress.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerPress.x;
      const deltaY = event.clientY - this.pointerPress.y;

      if (Math.hypot(deltaX, deltaY) > 9) {
        this.pointerPress.moved = true;
      }
    }

    trackPointerUp(event) {
      if (!this.pointerPress || this.pointerPress.pointerId !== event.pointerId) return;

      const press = this.pointerPress;
      this.pointerPress = null;

      if (press.moved) {
        this.suppressTriggerClick(press.trigger, 650);
      }
    }

    suppressTriggerClick(trigger, duration = 500) {
      if (!trigger) return;
      trigger.dataset.wiSuppressClickUntil = String(performance.now() + duration);
    }

    isTriggerClickSuppressed(trigger) {
      return Number(trigger?.dataset?.wiSuppressClickUntil || 0) > performance.now();
    }

    handleRootClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const scrollButton = target.closest('[data-scroll-to-testimonials]');
      if (scrollButton && this.contains(scrollButton)) {
        event.preventDefault();
        this.content?.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'center'
        });
        return;
      }

      const trigger = target.closest('[data-testimonial-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.isTriggerClickSuppressed(trigger)) return;
      this.openModal(Number(trigger.dataset.index || 0), trigger);
    }

    isModalOpen() {
      return Boolean(
        this.modal?.open
        || this.modal?.hasAttribute('open')
        || this.modal?.dataset.wiOpen === 'true'
      );
    }

    openModal(index, trigger) {
      if (!this.slides.length) return false;

      const now = performance.now();
      if (
        this.isModalOpen()
        && now - this.openedAt < 250
        && this.activeIndex === Number(index)
      ) {
        return false;
      }

      this.openedAt = now;
      this.lastFocusedElement = trigger || document.activeElement;
      this.renderSlide(index, false);

      this.modal.hidden = false;
      this.modal.dataset.wiOpen = 'true';

      try {
        if (typeof this.modal.showModal === 'function' && !this.modal.open) {
          this.modal.showModal();
        } else if (!this.modal.hasAttribute('open')) {
          this.modal.setAttribute('open', '');
        }
      } catch (error) {
        this.modal.setAttribute('open', '');
        this.modal.classList.add('wi-testimonial-stories__modal--fallback');
      }

      this.modal.style.setProperty('display', 'grid', 'important');
      this.modal.style.visibility = 'visible';

      document.documentElement.classList.add('wi-testimonial-modal-open');
      document.body.classList.add('wi-testimonial-modal-open');
      this.classList.add('is-modal-open');

      const video = this.getActiveVideo();
      if (video && this.shouldAutoplayVideos) {
        void this.playVideo(video, true);
      }

      requestAnimationFrame(() => {
        this.closeButton?.focus({ preventScroll: true });
      });

      return false;
    }

    closeModal() {
      if (!this.isModalOpen()) return;

      this.stopAllVideos(true);

      try {
        if (this.modal.open && typeof this.modal.close === 'function') {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
          this.afterModalClose();
        }
      } catch (error) {
        this.modal.removeAttribute('open');
        this.afterModalClose();
      }
    }

    afterModalClose() {
      if (!this.modal) return;

      this.modal.dataset.wiOpen = 'false';
      this.modal.classList.remove('wi-testimonial-stories__modal--fallback');
      this.modal.style.removeProperty('display');
      this.modal.style.removeProperty('visibility');

      document.documentElement.classList.remove('wi-testimonial-modal-open');
      document.body.classList.remove('wi-testimonial-modal-open');
      this.classList.remove('is-modal-open');

      const focusTarget = this.lastFocusedElement;
      this.lastFocusedElement = null;
      focusTarget?.focus?.({ preventScroll: true });
    }

    renderSlide(index, autoplay = true) {
      if (!this.slides.length) return;

      this.stopAllVideos(true);
      this.activeIndex = (Number(index) + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === this.activeIndex;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });

      if (this.counter) {
        this.counter.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
      }

      const video = this.getActiveVideo();
      if (video) {
        this.prepareVideo(video);
        this.setVideoUi(video, 'ready');

        if (autoplay && this.shouldAutoplayVideos && this.isModalOpen()) {
          void this.playVideo(video, true);
        }
      }
    }

    handleModalClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const closeTarget = target.closest('[data-modal-close]');
      if (closeTarget) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal();
        return;
      }

      const previous = target.closest('[data-modal-prev]');
      if (previous) {
        event.preventDefault();
        event.stopPropagation();
        this.renderSlide(this.activeIndex - 1);
        return;
      }

      const next = target.closest('[data-modal-next]');
      if (next) {
        event.preventDefault();
        event.stopPropagation();
        this.renderSlide(this.activeIndex + 1);
        return;
      }

      const startButton = target.closest('[data-video-start]');
      if (startButton) {
        event.preventDefault();
        event.stopPropagation();

        const video = startButton
          .closest('[data-video-shell]')
          ?.querySelector('.wi-testimonial-stories__video');

        if (video) void this.playVideo(video, true);
        return;
      }

      if (
        target.closest(
          '.wi-testimonial-stories__media-card, '
          + '.wi-testimonial-stories__product-link-wrap, '
          + '.wi-testimonial-stories__nav'
        )
      ) {
        return;
      }

      this.closeModal();
    }

    handleDocumentKeydown(event) {
      if (!this.isModalOpen()) return;

      const target = event.target instanceof Element ? event.target : null;
      const usesArrowKeys = target?.closest(
        'video, audio, input, select, textarea, [contenteditable="true"]'
      );

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
      } else if (event.key === 'ArrowLeft' && !usesArrowKeys) {
        event.preventDefault();
        this.renderSlide(this.activeIndex - 1);
      } else if (event.key === 'ArrowRight' && !usesArrowKeys) {
        event.preventDefault();
        this.renderSlide(this.activeIndex + 1);
      }
    }

    getVideoUi(video) {
      const shell = video?.closest('[data-video-shell]');

      return {
        startButton: shell?.querySelector('[data-video-start]'),
        errorMessage: shell?.querySelector('[data-video-error]')
      };
    }

    setVideoUi(video, state) {
      const { startButton, errorMessage } = this.getVideoUi(video);
      if (!startButton || !errorMessage) return;

      startButton.hidden = state === 'playing';
      startButton.textContent = state === 'error' ? 'Retry video' : 'Play video';
      errorMessage.hidden = state !== 'error';
    }

    prepareVideo(video) {
      if (!video) return;

      video.controls = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'metadata';

      if (
        video.readyState === 0
        || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
      ) {
        try {
          video.load();
        } catch (error) {
          /* The browser can load the source later when playback starts. */
        }
      }
    }

    async playVideo(video, allowMutedRetry = true) {
      if (!video || !this.isModalOpen()) return false;

      this.prepareVideo(video);

      try {
        await video.play();
        this.setVideoUi(video, 'playing');
        return true;
      } catch (firstError) {
        if (allowMutedRetry && !video.muted) {
          try {
            video.muted = true;
            video.defaultMuted = true;
            video.setAttribute('muted', '');
            await video.play();
            this.setVideoUi(video, 'playing');
            return true;
          } catch (secondError) {
            /* The explicit play button remains available. */
          }
        }

        this.setVideoUi(video, 'ready');
        return false;
      }
    }

    getActiveVideo() {
      return this.slides[this.activeIndex]
        ?.querySelector('.wi-testimonial-stories__video') || null;
    }

    stopAllVideos(reset = true) {
      this.videos.forEach((video) => {
        try {
          video.pause();
          if (reset) video.currentTime = 0;
        } catch (error) {
          /* Ignore media state errors while a slide is changing. */
        }

        this.setVideoUi(video, 'ready');
      });
    }

    bindVideo(video, signal) {
      this.setVideoUi(video, 'ready');

      video.addEventListener('play', () => {
        this.videos.forEach((otherVideo) => {
          if (otherVideo !== video) otherVideo.pause();
        });
        this.setVideoUi(video, 'playing');
      }, { signal });

      video.addEventListener('pause', () => {
        if (!video.ended && this.isModalOpen()) {
          this.setVideoUi(video, 'ready');
        }
      }, { signal });

      video.addEventListener('ended', () => {
        if (!video.loop) this.setVideoUi(video, 'ready');
      }, { signal });

      video.addEventListener('error', () => {
        this.setVideoUi(video, 'error');
      }, { signal });
    }

    destroy() {
      if (this.dataset.initialized !== 'true') return;

      this.stopRailAnimation(false);

      if (this.measureRafId) {
        cancelAnimationFrame(this.measureRafId);
        this.measureRafId = null;
      }

      if (this.scrollStateTimer) {
        clearTimeout(this.scrollStateTimer);
        this.scrollStateTimer = null;
      }

      this.resizeObserver?.disconnect();
      this.stopAllVideos(false);

      if (this.isModalOpen()) {
        try {
          if (this.modal.open && typeof this.modal.close === 'function') {
            this.modal.close();
          } else {
            this.modal.removeAttribute('open');
          }
        } catch (error) {
          this.modal.removeAttribute('open');
        }
      }

      document.documentElement.classList.remove('wi-testimonial-modal-open');
      document.body.classList.remove('wi-testimonial-modal-open');

      this.abortController?.abort();

      if (this.modal?.dataset.wiPortal === 'true') {
        this.modal.remove();
      }

      this.dataset.initialized = 'false';
    }
  }

  customElements.define('wi-testimonial-stories', WITestimonialStories);
}
